import React, { useState, useEffect } from "react";
import LoginPage from "./components/LoginPage";
import ClockInPage from "./components/ClockInPage";
import FieldApp from "./components/FieldApp";
import Sidebar from "./components/Sidebar";
import RevenuePage from "./components/RevenuePage";
import JobCostPage from "./components/JobCostPage";
import EmployeesPage from "./components/EmployeesPage";
import InProgressPage from "./components/InProgressPage";
import JobHistoryPage from "./components/JobHistoryPage";
import EquipmentPage from "./components/EquipmentPage";
import CustomersPage from "./components/CustomersPage";
import ExpensesPage from "./components/ExpensesPage";
import SuppliesPage from "./components/SuppliesPage";
import TimeClockPage from "./components/TimeClockPage";
import SettingsPage from "./components/SettingsPage";
import QRGeneratorPage from "./components/QRGeneratorPage";
import PDFCombinerPage from "./components/PDFCombinerPage";
import EtsyLabelBotPage from "./components/EtsyLabelBotPage";
import StoreSuppliesPage from "./components/StoreSuppliesPage";
import StoreRequestsPage from "./components/StoreRequestsPage";
import StoreSupportPage from "./components/StoreSupportPage";
import StoreDashboardPage from "./components/StoreDashboardPage";
import ProductsPage from "./components/ProductsPage";
import InventoryPage from "./components/InventoryPage";
import StoreRevenuePage from "./components/StoreRevenuePage";
import FinancingPage from "./components/FinancingPage";
import ReverbPage from "./components/ReverbPage";
import AmazonSellerPage from "./components/AmazonSellerPage";
import TikTokPage from "./components/TikTokPage";
import ShopifyPage from "./components/ShopifyPage";
import EbayPage from "./components/EbayPage";
import ListingMigratorPage from "./components/ListingMigratorPage";
import FilamentUsagePage from "./components/FilamentUsagePage";
import EtsyIntegrationPage from "./components/EtsyIntegrationPage";
import StoreTrendsPage from "./components/StoreTrendsPage";
import PlaceholderPage from "./components/PlaceholderPage";
import InstantQuotingPage from "./components/InstantQuotingPage";
import TourRequestsPage from "./components/TourRequestsPage";
import ProductDevelopmentPage from "./components/ProductDevelopmentPage";
import { sGet, sSet, useIsMobile } from "./utils/helpers";
import { DEFAULT_EMPLOYEES, JOBS_KEY, PROGRESS_KEY, TIMECLOCK_KEY, STORE_REVENUE_KEY } from "./data/defaults";
import { generateDemoJobs, generateDemoProgress } from "./data/demo";
import S from "./styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";
// Dev-only: start the dev server with REACT_APP_DEV_AUTOLOGIN=1 to skip the
// login screen (signed in as employee 01). Double-gated on NODE_ENV so a
// stray env var can never enable it in a production build.
const DEV_AUTOLOGIN = process.env.NODE_ENV === "development" && process.env.REACT_APP_DEV_AUTOLOGIN === "1";
const DEMO_JOBS = generateDemoJobs();
const DEMO_PROGRESS = generateDemoProgress();

// URL ↔ Section mapping
const SECTION_PATHS = {
  revenue: "/revenue", expenses: "/expenses", cutcost: "/calculator", progress: "/open-jobs",
  history: "/job-history", customers: "/customers", employees: "/employees", timeclock: "/time-clock",
  equipment: "/equipment", supplies: "/materials", instantquoting: "/instant-quoting", "tour-requests": "/tour-requests", "product-dev": "/product-development", qrgenerator: "/qr-generator", pdfcombiner: "/pdf-combiner", etsylabelbot: "/etsy-label-bot", settings: "/settings",
  "store-dashboard": "/store-dashboard", "store-revenue": "/store-revenue", "store-financing": "/store-financing", "store-expenses": "/store-expenses", "store-products": "/store-products", "store-inventory": "/store-inventory", "store-support": "/store-support", "store-requests": "/store-requests", "store-supplies": "/store-supplies", "store-trends": "/store-trends", "store-reverb": "/store-reverb", "store-etsy": "/store-etsy", "store-amazon": "/store-amazon", "store-tiktok": "/store-tiktok", "store-shopify": "/store-shopify", "store-ebay": "/store-ebay", "store-listing-migrator": "/store-listing-migrator", "store-filament": "/store-filament",
};
const PATH_TO_SECTION = Object.fromEntries(Object.entries(SECTION_PATHS).map(([k, v]) => [v, k]));
const STORE_SECTIONS = ["store-dashboard", "store-revenue", "store-financing", "store-trends", "store-expenses", "store-products", "store-inventory", "store-support", "store-requests", "store-supplies", "store-reverb", "store-etsy", "store-amazon", "store-tiktok", "store-shopify", "store-ebay", "store-listing-migrator", "store-filament"];

function parseUrl() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const urlStore = params.get("store") || "nycmfg";
  const section = PATH_TO_SECTION[path];
  if (section) return { store: STORE_SECTIONS.includes(section) ? (urlStore !== "nycmfg" ? urlStore : "designshop") : urlStore, section };
  return { store: "nycmfg", section: "revenue" };
}

export default function App() {
  const isMobile = useIsMobile(768);
  const [loggedIn, setLoggedIn] = useState(() => {
    if (DEV_AUTOLOGIN) {
      sessionStorage.setItem("nycmfg-auth", "true");
      if (!sessionStorage.getItem("nycmfg-user")) sessionStorage.setItem("nycmfg-user", JSON.stringify({ empId: "01", name: "Daniel" }));
      return true;
    }
    return sessionStorage.getItem("nycmfg-auth") === "true";
  });
  const initial = parseUrl();
  const [store, setStoreRaw] = useState(initial.store);
  const [section, setSectionRaw] = useState(initial.section);

  const setSection = (s) => {
    setSectionRaw(s);
    const storePath = store !== "nycmfg" ? `?store=${store}` : "";
    const path = SECTION_PATHS[s] || "/";
    window.history.pushState({}, "", path + storePath);
  };

  const setStore = (s) => {
    setStoreRaw(s);
    const newSection = s === "nycmfg" ? "revenue" : "store-dashboard";
    setSectionRaw(newSection);
    const storePath = s !== "nycmfg" ? `?store=${s}` : "";
    window.history.pushState({}, "", (SECTION_PATHS[newSection] || "/") + storePath);
  };

  // Handle browser back/forward
  useEffect(() => {
    const handlePop = () => {
      const { store: s, section: sec } = parseUrl();
      setStoreRaw(s);
      setSectionRaw(sec);
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar-collapsed-v1") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sidebar-collapsed-v1", sidebarCollapsed ? "1" : "0"); } catch {}
  }, [sidebarCollapsed]);
  const [navBadges, setNavBadges] = useState({});
  const [timeEntries, setTimeEntries] = useState([]);

  // Sidebar notification counts. Polled every 60s + refreshed when the
  // user navigates to a page that would consume the count.
  const fetchNavBadges = React.useCallback(() => {
    fetch(`${API_URL}/api/tour-requests?status=new`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setNavBadges((prev) => ({ ...prev, "tour-requests": data.length }));
        }
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    fetchNavBadges();
    const id = setInterval(fetchNavBadges, 60000);
    return () => clearInterval(id);
  }, [fetchNavBadges]);
  // Re-fetch badges when leaving Tour Requests (count drops to 0 once
  // they've been triaged, no need to wait for the next poll).
  useEffect(() => {
    if (section !== "tour-requests") fetchNavBadges();
  }, [section, fetchNavBadges]);
  // Store-specific data: keyed by store id
  const [storeExpenses, setStoreExpenses] = useState({});
  const [storeHardware, setStoreHardware] = useState({});
  const [storeShipping, setStoreShipping] = useState({});
  const [storeRequests, setStoreRequests] = useState({});
  const [storeSupport, setStoreSupport] = useState({});
  const [storeSupportArchive, setStoreSupportArchive] = useState({});
  const [storeRevenue, setStoreRevenue] = useState({}); // eslint-disable-line no-unused-vars
  const [materials, setMaterials] = useState([]);
  const [employees, setEmployees] = useState(DEFAULT_EMPLOYEES);

  // Fetch employees from Postgres
  const fetchEmployees = React.useCallback(() => {
    fetch(`${API_URL}/api/employees`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data) && data.length > 0) setEmployees(data); })
      .catch(() => {});
  }, []);
  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  // Fetch expenses from Postgres
  const fetchExpenses = React.useCallback(() => {
    fetch(`${API_URL}/api/expenses`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setExpensesList(data); })
      .catch(() => {});
  }, []);
  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  // Fetch equipment from Postgres
  const fetchEquipment = React.useCallback(() => {
    fetch(`${API_URL}/api/equipment`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setEquipment(data); })
      .catch(() => {});
  }, []);
  useEffect(() => { fetchEquipment(); }, [fetchEquipment]);

  // Fetch maintenance log from Postgres
  const fetchMaintenance = React.useCallback(() => {
    fetch(`${API_URL}/api/maintenance`)
      .then((r) => r.json())
      .then((data) => { if (data && typeof data === "object") setMaintenanceLog(data); })
      .catch(() => {});
  }, []);
  useEffect(() => { fetchMaintenance(); }, [fetchMaintenance]);

  // Fetch customers from Postgres
  const fetchCustomers = React.useCallback(() => {
    fetch(`${API_URL}/api/customers`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setCustomers(data); })
      .catch(() => {});
  }, []);
  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // Fetch store expenses from Postgres
  const fetchStoreExpenses = React.useCallback(() => {
    Promise.all([
      fetch(`${API_URL}/api/store-expenses?store=designshop`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/store-expenses?store=vibratovault`).then((r) => r.json()).catch(() => []),
    ]).then(([ds, vv]) => {
      setStoreExpenses({ designshop: Array.isArray(ds) ? ds : [], vibratovault: Array.isArray(vv) ? vv : [] });
    });
  }, []);
  useEffect(() => { fetchStoreExpenses(); }, [fetchStoreExpenses]);

  // Fetch HQ supplies from Postgres
  const fetchMaterials = React.useCallback(() => {
    fetch(`${API_URL}/api/materials`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setMaterials(d); }).catch(() => {});
  }, []);
  useEffect(() => { fetchMaterials(); }, [fetchMaterials]);

  const fetchFilament = React.useCallback(() => {
    fetch(`${API_URL}/api/filament`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setFilament(d); }).catch(() => {});
  }, []);
  useEffect(() => { fetchFilament(); }, [fetchFilament]);

  const fetchHardware = React.useCallback(() => {
    fetch(`${API_URL}/api/hardware?location=nycmfg`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setHardware(d); }).catch(() => {});
  }, []);
  useEffect(() => { fetchHardware(); }, [fetchHardware]);

  const fetchShipping = React.useCallback(() => {
    fetch(`${API_URL}/api/shipping-supplies?location=nycmfg`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setShippingSupplies(d); }).catch(() => {});
  }, []);
  useEffect(() => { fetchShipping(); }, [fetchShipping]);

  // Fetch store hardware + shipping from Postgres
  const fetchStoreHardware = React.useCallback(() => {
    Promise.all([
      fetch(`${API_URL}/api/hardware?location=designshop`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/hardware?location=vibratovault`).then((r) => r.json()).catch(() => []),
    ]).then(([ds, vv]) => {
      setStoreHardware({ designshop: Array.isArray(ds) ? ds : [], vibratovault: Array.isArray(vv) ? vv : [] });
    });
  }, []);
  useEffect(() => { fetchStoreHardware(); }, [fetchStoreHardware]);

  const fetchStoreShipping = React.useCallback(() => {
    Promise.all([
      fetch(`${API_URL}/api/shipping-supplies?location=designshop`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/shipping-supplies?location=vibratovault`).then((r) => r.json()).catch(() => []),
    ]).then(([ds, vv]) => {
      setStoreShipping({ designshop: Array.isArray(ds) ? ds : [], vibratovault: Array.isArray(vv) ? vv : [] });
    });
  }, []);
  useEffect(() => { fetchStoreShipping(); }, [fetchStoreShipping]);

  const [equipment, setEquipment] = useState([]);
  const [maintenanceLog, setMaintenanceLog] = useState({});
  const [customers, setCustomers] = useState([]);
  const [filament, setFilament] = useState([]);
  const [hardware, setHardware] = useState([]);
  const [shippingSupplies, setShippingSupplies] = useState([]);
  const [expensesList, setExpensesList] = useState([]);
  const [jobs, setJobs] = useState(DEMO_JOBS);
  const [progressJobs, setProgressJobs] = useState(DEMO_PROGRESS);
  const [editingJob, setEditingJob] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [jobCounter, setJobCounter] = useState(1050);

  useEffect(() => {
    Promise.all([
      sGet(JOBS_KEY),
      sGet(PROGRESS_KEY),
    ]).then(([j, p]) => {
      if (j && j.length > 0) setJobs(j);
      if (p && p.length > 0) setProgressJobs(p);
      setLoaded(true);
    });
    // Clean up old localStorage keys now managed by Postgres
    try { localStorage.removeItem("customers-db-v1"); localStorage.removeItem("expenses-db-v1"); localStorage.removeItem("store-expenses-v1"); localStorage.removeItem("equipment-db-v1"); localStorage.removeItem("maintenance-log-v1"); localStorage.removeItem("materials-db-v2"); localStorage.removeItem("filament-db-v1"); localStorage.removeItem("hardware-db-v1"); localStorage.removeItem("shipping-supplies-db-v1"); localStorage.removeItem("store-hardware-v1"); localStorage.removeItem("store-shipping-v1"); } catch (e) {}
    // Load time clock + store data
    sGet(TIMECLOCK_KEY).then((tc) => { if (tc && tc.length > 0) setTimeEntries(tc); });
    sGet(STORE_REVENUE_KEY).then((sr) => {
      if (sr && typeof sr === "object") setStoreRevenue(sr);
    });
    sGet("store-requests-v1").then((sr) => { if (sr && typeof sr === "object") setStoreRequests(sr); });
    sGet("store-support-v1").then((ss) => { if (ss && typeof ss === "object") setStoreSupport(ss); });
    sGet("store-support-archive-v1").then((sa) => { if (sa && typeof sa === "object") setStoreSupportArchive(sa); });
  }, []);

  useEffect(() => {
    if (loaded) {
      sSet(JOBS_KEY, jobs);
      sSet(PROGRESS_KEY, progressJobs);
      sSet(TIMECLOCK_KEY, timeEntries);
      sSet(STORE_REVENUE_KEY, storeRevenue);
      sSet("store-requests-v1", storeRequests);
      sSet("store-support-v1", storeSupport);
      sSet("store-support-archive-v1", storeSupportArchive);
    }
  }, [jobs, progressJobs, timeEntries, storeRevenue, storeRequests, storeSupport, storeSupportArchive, loaded]);

  const handleSaveJob = (data) => {
    const num = jobCounter + 1;
    setJobCounter(num);
    const newJob = { ...data, id: Date.now(), jobNumber: `JOB-${String(num).padStart(4, "0")}`, dateOpened: new Date().toISOString().slice(0, 10) };
    setProgressJobs((p) => [...p, newJob]);
  };

  const handleUpdateJob = (updatedJob) => {
    setProgressJobs((p) => p.map((j) => (j.id === updatedJob.id ? updatedJob : j)));
    setEditingJob(updatedJob);
  };

  const handleApprove = (id, restoredJob) => {
    if (restoredJob) {
      setProgressJobs((p) => [...p, restoredJob]);
      return;
    }
    const job = progressJobs.find((j) => j.id === id);
    if (!job) return;
    const historyEntry = {
      id: Date.now(),
      jobNumber: job.jobNumber,
      date: new Date().toISOString().slice(0, 10),
      customer: job.customerName,
      customerEmail: job.customerEmail || "",
      customerPhone: job.customerPhone || "",
      customerCost: job.customerCost,
      ourCost: job.ourCost,
      profit: job.profit,
      lineItems: job.lineItems || [],
      laborHours: job.laborHours || "",
      laborRate: job.laborRate || "",
      selectedEmployee: job.selectedEmployee || "",
      jobType: job.jobType || "",
    };
    setJobs((j) => [...j, historyEntry]);
    setProgressJobs((p) => p.filter((j) => j.id !== id));
  };

  const handleCancel = (id) => {
    setProgressJobs((p) => p.filter((j) => j.id !== id));
  };

  const handleOpenJob = (job) => {
    setEditingJob(job);
    setSection("cutcost");
  };

  const renderContent = () => {
    switch (section) {
      case "revenue":
        return (
          <main style={S.main}>
            <RevenuePage jobs={jobs} progressJobs={progressJobs} expenses={expensesList} />
          </main>
        );
      case "cutcost":
        return (
          <JobCostPage
            materials={materials}
            filament={filament}
            employees={employees}
            editingJob={editingJob}
            onSaveJob={handleSaveJob}
            onUpdateJob={handleUpdateJob}
            onClearEdit={() => setEditingJob(null)}
          />
        );
      case "expenses":
        return (
          <main style={S.main}>
            <ExpensesPage expenses={expensesList} setExpenses={setExpensesList} />
          </main>
        );
      case "progress":
        return (
          <main style={S.main}>
            <InProgressPage
              progressJobs={progressJobs}
              onApprove={handleApprove}
              onCancel={handleCancel}
              onOpenJob={handleOpenJob}
            />
          </main>
        );
      case "history":
        return (
          <main style={S.main}>
            <JobHistoryPage jobs={jobs} employees={employees} />
          </main>
        );
      case "employees":
        return (
          <main style={S.main}>
            <EmployeesPage employees={employees} setEmployees={setEmployees} onRefresh={fetchEmployees} />
          </main>
        );
      case "timeclock":
        return (
          <main style={S.main}>
            <TimeClockPage employees={employees} currentEmpId={currentUserSession?.empId} />
          </main>
        );
      case "customers":
        return (
          <main style={S.main}>
            <CustomersPage
              customers={customers}
              setCustomers={setCustomers}
              jobs={jobs}
              progressJobs={progressJobs}
            />
          </main>
        );
      case "equipment":
        return (
          <main style={S.main}>
            <EquipmentPage
              equipment={equipment}
              setEquipment={setEquipment}
              maintenanceLog={maintenanceLog}
              setMaintenanceLog={setMaintenanceLog}
            />
          </main>
        );
      case "supplies":
        return (
          <SuppliesPage
            materials={materials} setMaterials={setMaterials}
            filament={filament} setFilament={setFilament}
            hardware={hardware} setHardware={setHardware}
            shippingSupplies={shippingSupplies} setShippingSupplies={setShippingSupplies}
          />
        );
      case "instantquoting":
        return (
          <main style={S.main}>
            <InstantQuotingPage />
          </main>
        );
      case "tour-requests":
        return (
          <main style={S.main}>
            <TourRequestsPage />
          </main>
        );
      case "product-dev":
        return (
          <main style={S.main}>
            <ProductDevelopmentPage employees={employees} />
          </main>
        );
      case "qrgenerator":
        return (
          <main style={S.main}>
            <QRGeneratorPage />
          </main>
        );
      case "pdfcombiner":
        return (
          <main style={S.main}>
            <PDFCombinerPage />
          </main>
        );
      case "etsylabelbot":
        return (
          <main style={S.main}>
            <EtsyLabelBotPage />
          </main>
        );
      case "settings":
        return (
          <main style={S.main}>
            <SettingsPage employees={employees} onRefresh={fetchEmployees} store={store} />
          </main>
        );
      case "store-dashboard": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        return (
          <main style={S.main}>
            <StoreDashboardPage store={store} storeName={sName} onNavigate={setSection} />
          </main>
        );
      }
      case "store-revenue": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        return (
          <main style={S.main}>
            <StoreRevenuePage storeName={sName} store={store} />
          </main>
        );
      }
      case "store-trends": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        return (
          <main style={S.main}>
            <StoreTrendsPage store={store} storeName={sName} />
          </main>
        );
      }
      case "store-financing": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        return (
          <main style={S.main}>
            <FinancingPage store={store} storeName={sName} />
          </main>
        );
      }
      case "store-expenses": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        const exp = storeExpenses[store] || [];
        const setExp = (data) => setStoreExpenses({ ...storeExpenses, [store]: data });
        return (
          <main style={S.main}>
            <ExpensesPage expenses={exp} setExpenses={setExp} storeTitle={sName} store={store} hideReimbursements={store === "vibratovault"} />
          </main>
        );
      }
      case "store-products": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        return (
          <main style={S.mainWide}>
            <ProductsPage store={store} storeName={sName} />
          </main>
        );
      }
      case "store-inventory": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        return (
          <main style={S.main}>
            <InventoryPage store={store} storeName={sName} />
          </main>
        );
      }
      case "store-support": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        const tix = storeSupport[store] || [];
        const setTix = (data) => setStoreSupport({ ...storeSupport, [store]: data });
        const arch = storeSupportArchive[store] || [];
        const setArch = (data) => setStoreSupportArchive({ ...storeSupportArchive, [store]: data });
        return (
          <main style={S.main}>
            <StoreSupportPage storeName={sName} store={store} tickets={tix} setTickets={setTix} archivedTickets={arch} setArchivedTickets={setArch} employees={employees} />
          </main>
        );
      }
      case "store-requests": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        const reqs = storeRequests[store] || [];
        const setReqs = (data) => setStoreRequests({ ...storeRequests, [store]: data });
        return (
          <main style={S.main}>
            <StoreRequestsPage storeName={sName} requests={reqs} setRequests={setReqs} />
          </main>
        );
      }
      case "store-supplies": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        const hw = storeHardware[store] || [];
        const setHw = (data) => setStoreHardware({ ...storeHardware, [store]: data });
        const sh = storeShipping[store] || [];
        const setSh = (data) => setStoreShipping({ ...storeShipping, [store]: data });
        return (
          <main style={S.main}>
            <StoreSuppliesPage storeName={sName} store={store} hardware={hw} setHardware={setHw} shippingSupplies={sh} setShippingSupplies={setSh} />
          </main>
        );
      }
      case "store-reverb":
        return (
          <main style={S.main}>
            <ReverbPage store={store} />
          </main>
        );
      case "store-etsy":
        return (
          <main style={S.main}>
            <EtsyIntegrationPage store={store} />
          </main>
        );
      case "store-amazon":
        return (
          <main style={S.main}>
            <AmazonSellerPage store={store} />
          </main>
        );
      case "store-tiktok":
        return (
          <main style={S.main}>
            <TikTokPage store={store} />
          </main>
        );
      case "store-shopify":
        return (
          <main style={S.main}>
            <ShopifyPage store={store} />
          </main>
        );
      case "store-ebay":
        return (
          <main style={S.main}>
            <EbayPage store={store} />
          </main>
        );
      case "store-listing-migrator":
        return (
          <main style={S.main}>
            <ListingMigratorPage store={store} />
          </main>
        );
      case "store-filament": {
        const storeNames = { designshop: "Design Shop", vibratovault: "Vibrato Vault" };
        const sName = storeNames[store] || store;
        return (
          <main style={S.main}>
            <FilamentUsagePage store={store} storeName={sName} />
          </main>
        );
      }
      default:
        return (
          <main style={S.main}>
            <PlaceholderPage section={section} />
          </main>
        );
    }
  };

  const handleLogin = (emp) => {
    sessionStorage.setItem("nycmfg-auth", "true");
    if (emp) sessionStorage.setItem("nycmfg-user", JSON.stringify(emp));
    // Navigate to first permitted section
    const perms = emp?.permissions || [];
    if (perms.length > 0) {
      setSectionRaw(perms[0]);
      const path = SECTION_PATHS[perms[0]] || "/";
      window.history.pushState({}, "", path);
    }
    setLoggedIn(true);
  };

  // Standalone pages — no admin login required
  if (window.location.pathname === "/employeetc") {
    return <ClockInPage />;
  }
  if (window.location.pathname === "/field") {
    return <FieldApp />;
  }

  if (!loggedIn) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
          *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}body{background:#F4F2EC}
        `}</style>
        <LoginPage onLogin={handleLogin} />
      </>
    );
  }

  // Get live permissions from employees array (not stale sessionStorage)
  const currentUserSession = (() => { try { return JSON.parse(sessionStorage.getItem("nycmfg-user")); } catch { return null; } })();
  const liveEmployee = currentUserSession ? employees.find((e) => String(e.empId) === String(currentUserSession.empId)) : null;
  const userPermissions = liveEmployee?.permissions || currentUserSession?.permissions || null;

  return (
    <div style={{ ...S.root, display: isMobile ? "block" : "flex" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}body{background:#F4F2EC}
        input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{opacity:1}
        ::selection{background:#0033FF;color:#F4F2EC}::-webkit-scrollbar{width:6px;height:6px}
        ::-webkit-scrollbar-track{background:#F4F2EC}::-webkit-scrollbar-thumb{background:#0A0A0F;border-radius:3px}
        @keyframes slideUp{from{transform:translateX(-50%) translateY(20px);opacity:0}to{transform:translateX(-50%) translateY(0);opacity:1}}
        @media(max-width:768px){
          body{overflow-x:hidden;-webkit-text-size-adjust:100%;}
          input,select,textarea{font-size:16px!important;}
          table{font-size:13px!important;width:100%!important;}
          th,td{padding:6px 8px!important;font-size:13px!important;}
          h2{font-size:18px!important;}
          .chart-grid{grid-template-columns:1fr!important;}
          .calc-layout{flex-direction:column!important;}
          .calc-layout>div{width:100%!important;flex:none!important;position:static!important;top:auto!important;}
          .selection-grid{grid-template-columns:1fr!important;}
          .detail-grid{grid-template-columns:1fr!important;}
          .nav-pills{overflow-x:auto!important;-webkit-overflow-scrolling:touch;}
        }
      `}</style>
      <Sidebar section={section} setSection={setSection} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} isMobile={isMobile} store={store} setStore={setStore} userPermissions={userPermissions} badges={navBadges} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      <div style={isMobile ? { width: "100%", minHeight: "100vh" } : { ...S.contentArea, marginLeft: sidebarCollapsed ? 64 : 260, transition: "margin-left .18s ease" }}>
        {renderContent()}
      </div>
    </div>
  );
}
